import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import BottomSheet from "components/layout/motion/BottomSheet";
import type { RobotEthernetStatus, RobotProfileCatalogEntry, RobotProfileId } from "lib/robotProfileApi";
import type { RobotProfileDraft } from "lib/consts/robotProfiles";
import { resolveDefaults } from "lib/consts/robotProfiles";

type Props = {
    visible: boolean;
    profileId: RobotProfileId | null;
    catalog: Record<RobotProfileId, RobotProfileCatalogEntry>;
    draft: RobotProfileDraft | null;
    ethernetStatus: RobotEthernetStatus;
    busy: boolean;
    onClose: () => void;
    onChangeMethod: (method: string) => void;
    onChangeParam: (key: string, value: string) => void;
    onSave: () => void;
};

const RobotNetworkConfigSheet: React.FC<Props> = ({
    visible,
    profileId,
    catalog,
    draft,
    ethernetStatus,
    busy,
    onClose,
    onChangeMethod,
    onChangeParam,
    onSave,
}) => {
    const { t } = useTranslation(["settings", "common"]);

    const fieldKeys = useMemo(() => {
        if (!profileId || !draft) return [];
        const keys = Object.keys(draft.params);
        if (keys.length > 0) return keys;
        return Object.keys(resolveDefaults(catalog, profileId, draft.method).params);
    }, [catalog, draft, profileId]);

    const showEthernetPanel =
        Boolean(draft) &&
        (draft!.method === "ethernet" ||
            draft!.method === "udp" ||
            draft!.method === "sdk" ||
            profileId === "lynx" ||
            profileId === "tron1" ||
            profileId === "spot" ||
            Boolean(draft!.params.local_ip));

    return (
        <BottomSheet
            visible={visible && profileId != null && draft != null}
            onClose={onClose}
            className="mx-auto flex w-full max-w-xl flex-col border border-black80 bg-black100 shadow-lg"
            zIndexClassName="z-[120]"
        >
            {profileId && draft ? (
                <>
                    <div className="flex shrink-0 items-center justify-between border-b border-black80 px-4 py-3">
                        <div>
                            <p className="text-h5 font-semibold text-white">
                                {t("robot.configureTitle", {
                                    profile: t(`robot.options.${profileId}.title`),
                                    defaultValue: `Configure {{profile}}`,
                                })}
                            </p>
                            <p className="mt-1 text-h5 text-black40">
                                {t("robot.configureHint", {
                                    defaultValue:
                                        "Set connection method and network parameters. Save to persist without connecting.",
                                })}
                            </p>
                        </div>
                    </div>

                    <form
                        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
                        onSubmit={(e) => {
                            e.preventDefault();
                            onSave();
                        }}
                    >
                        <div>
                            <p className="text-h5 font-semibold text-white">
                                {t("robot.connectionMethod")}
                            </p>
                            <select
                                className="mt-2 w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white"
                                value={draft.method}
                                onChange={(e) => onChangeMethod(e.target.value)}
                                disabled={busy}
                                aria-label={t("robot.connectionMethod")}
                            >
                                {(catalog[profileId]?.methods || []).map((method) => (
                                    <option key={method} value={method}>
                                        {t(`robot.methods.${method}`, { defaultValue: method })}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="grid grid-cols-1 gap-2">
                            {fieldKeys.map((key) => (
                                <label key={key} className="flex flex-col gap-1">
                                    <span className="text-h5 text-black40">
                                        {t(`robot.fields.${key}`, { defaultValue: key })}
                                    </span>
                                    <input
                                        type={
                                            key.includes("key") || key.includes("password")
                                                ? "password"
                                                : key === "avoidance_m"
                                                  ? "number"
                                                  : "text"
                                        }
                                        autoComplete="off"
                                        className="w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white"
                                        value={draft.params[key] ?? ""}
                                        onChange={(e) => onChangeParam(key, e.target.value)}
                                        disabled={busy}
                                        {...(key === "avoidance_m"
                                            ? { min: 0, max: 2, step: 0.01 }
                                            : {})}
                                    />
                                </label>
                            ))}
                        </div>

                        {showEthernetPanel ? (
                            <div className="rounded-r5 border border-black80 bg-black90 p-3">
                                <p className="text-h5 font-semibold text-white">
                                    {t("robot.ethernet.title")}
                                </p>
                                <p className="mt-1 text-h5 text-black40">{t("robot.ethernet.hint")}</p>
                                {draft.params.local_ip ? (
                                    <p className="mt-1 text-h5 text-black40">
                                        {t("robot.ethernet.desiredIp", {
                                            ip: draft.params.local_ip,
                                            defaultValue: `Desired local IP: ${draft.params.local_ip}`,
                                        })}
                                    </p>
                                ) : null}
                                {ethernetStatus?.error ? (
                                    <p className="mt-1 text-h5 text-red-400">{ethernetStatus.error}</p>
                                ) : (
                                    <p className="mt-1 text-h5 text-black40">
                                        {t("robot.ethernet.statusLine", {
                                            iface: ethernetStatus?.interface || "enP8p1s0",
                                            state: ethernetStatus?.state || t("robot.ethernet.unknown"),
                                            ipv4: ethernetStatus?.ipv4 || t("robot.ethernet.noAddress"),
                                        })}
                                    </p>
                                )}
                            </div>
                        ) : null}

                        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-black80 pt-3">
                            <FunctionalButton
                                title={t("common:cancel")}
                                type="button"
                                className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40 disabled:opacity-50"
                                disabled={busy}
                                onClick={onClose}
                            />
                            <FunctionalButton
                                title={busy ? t("common:saving") : t("common:save")}
                                type="submit"
                                className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                                disabled={busy}
                            />
                        </div>
                    </form>
                </>
            ) : null}
        </BottomSheet>
    );
};

export default RobotNetworkConfigSheet;
